import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetRiskAssessmentResultConfigByIds } from '@/hooks/queries/risk-assessment-result-config/useGetRiskAssessmentResultConfigByIds';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';

import { useControlTypeLabel } from '../../../../assessments/forms/useControlTypeLabel';
import { UNRATED } from '../../../../controls/lookupData';
import type {
  RiskAssessmentResultFlatFields,
  RiskAssessmentResultRegisterFields,
} from './types';

export const useRiskRatingLabelledFields = (
  riskId: string,
  records: RiskAssessmentResultFlatFields[] | undefined,
  configIds: string[]
) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'ratings' });
  const statusRating = useRating('assessment_status');
  const getControlTypeLabel = useControlTypeLabel();
  const { versionById } = useGetRiskAssessmentResultConfigByIds(configIds);

  const { resolveRiskRating, resolveImpact, resolveLikelihood } =
    useRiskRatingResolver('standard', {
      configType: 'risk_assessment',
      configIds,
    });

  return useMemo<RiskAssessmentResultRegisterFields[]>(() => {
    return (
      records?.map((d) => {
        const assessment = d.parents.find((p) => p.assessment)?.assessment;

        return {
          ...d,
          Methodology:
            d.impacts && d.impacts.length > 0
              ? t('methodology.multiImpact')
              : t('methodology.impactLikelihood'),
          Version:
            d.ConfigId && versionById.has(d.ConfigId)
              ? `v${versionById.get(d.ConfigId)}`
              : '-',
          StatusLabelled:
            assessment !== undefined
              ? (statusRating.getLabel(assessment?.Status) ?? '-')
              : '-',
          NextTestDate: assessment?.NextTestDate ?? '-',
          ActualCompletionDate: assessment?.ActualCompletionDate ?? '-',
          ParentTitle: assessment?.Title ?? '-',
          ControlTypeLabelled:
            getControlTypeLabel(d.ControlType) || UNRATED.label,
          CompletionDate: assessment?.ActualCompletionDate ?? '-',
          RatingLabelled:
            resolveRiskRating({
              likelihood: d.Likelihood,
              impact: d.Impact,
              controlType: d.ControlType,
              rating: d.Rating,
              configId: d.ConfigId,
            })?.label ?? UNRATED.label,
          ImpactLabelled:
            resolveImpact(d.Impact, d.ConfigId)?.label ?? UNRATED.label,
          LikelihoodLabelled:
            resolveLikelihood(d.Likelihood, d.ConfigId)?.label ?? UNRATED.label,
        };
      }) || []
    );
  }, [
    records,
    t,
    statusRating,
    getControlTypeLabel,
    resolveRiskRating,
    resolveImpact,
    resolveLikelihood,
    versionById,
  ]);
};
